import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    Alert,
    Box,
    Chip,
    Dialog,
    DialogContent,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DevicesOutlinedIcon from '@mui/icons-material/DevicesOutlined';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import LinkRoundedIcon from '@mui/icons-material/Link';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded';
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TuneIcon from '@mui/icons-material/Tune';
import WebOutlinedIcon from '@mui/icons-material/WebOutlined';
import SectionCard from '../../../../_helpers/reusableComponents/design/SectionCard';
import KpiCard from '../../../../_helpers/reusableComponents/design/KpiCard';
import EmptyState from '../../../../_helpers/reusableComponents/design/EmptyState';
import {
    ChoroplethMap,
    ColumnChart,
    DonutChart,
    RankedBars,
    TrendChart,
    foldTail,
} from '../../../../_helpers/reusableComponents/design/Charts';
import {
    chartPalette,
    chartSeries,
    motion,
    palette,
    radius,
    shadow,
    typography,
} from '../../../../_helpers/reusableComponents/design/designTokens';
import { handleSongList } from '../../../../_services/creater/creatorSongList.service';
import {
    LINK_ANALYTICS_BREAKDOWNS,
    LINK_ANALYTICS_DAY_OPTIONS,
    LINK_ANALYTICS_HERO_FIELDS,
    LINK_ANALYTICS_SECONDARY_FIELDS,
    NOT_SHAREABLE_MESSAGE,
    SHARE_SURFACE,
    formatLinkMetric,
    getShareLink,
    getShareLinkAnalytics,
    getShareLinkQr,
    getShareLinkStatus,
    groupLinkSources,
    hasShareLink,
    linkAnalyticsReasonCopy,
    revokeShareLink,
    updateShareLinkAlias,
} from '../../../../_services/creater/shareLink.service';

// The two lines on the performance chart. Same unit — a count of people — which
// is what lets them share one axis.
const SERIES = [chartSeries.clicks, chartSeries.uniqueVisitors];

// Icons and accents for the four headline numbers, keyed by the same field name
// the constants use, so the row is driven entirely by that list and adding a
// field there cannot leave a card without an icon by accident.
//
// One accent for all four. The tile colour here carries NO meaning — the label
// beside it says which number this is, and the four were never a scale or a set
// of states. Red, blue, green and amber were four claims the data does not
// make; four shades of the brand instead were four answers to a question nobody
// asked, and the darkest of them read as brown. The icon distinguishes the
// cards; the colour just says whose product this is.
const HERO = {
    totalClicks: { icon: <TouchAppOutlinedIcon /> },
    uniqueVisitors: { icon: <PeopleAltOutlinedIcon /> },
    conversions: { icon: <CheckCircleOutlineIcon /> },
    conversionRate: { icon: <TrendingUpIcon /> },
};

// Icons for the secondary strip. Muted, small, and set beside the label rather
// than in a coloured tile: this row sits UNDER the four headline cards, and
// giving it the same weight would leave the screen with eight things all
// claiming to be the point.
const REACH_ICONS = {
    uniqueCountriesCount: <PublicOutlinedIcon sx={{ fontSize: 15 }} />,
    uniqueDevicesCount: <DevicesOutlinedIcon sx={{ fontSize: 15 }} />,
    uniqueBrowsersCount: <WebOutlinedIcon sx={{ fontSize: 15 }} />,
    totalConversionValue: <PaidOutlinedIcon sx={{ fontSize: 15 }} />,
};

// MUI's `severity="info"` paints itself pale blue with a blue glyph, which is
// the one colour on these screens that belongs to no part of this product. The
// states it carries — "no gn2.me code yet", "the provider is quiet" — are the
// brand's own news, so they wear the brand's own softest tint.
const NOTICE_SX = {
    borderRadius: `${radius.md}px`,
    bgcolor: palette.brandSofter,
    border: `1px solid ${palette.brandSoft}`,
    color: palette.inkSoft,
    fontSize: 13,
    '& .MuiAlert-icon': { color: palette.brand },
};

// The alias rule the backend validates against, checked here too so a typo is
// caught before a round trip.
const ALIAS_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{2,31}$/;

// Which tab is showing is part of the address, not just component state: a
// refresh, a bookmark or a link pasted to someone else all landed on the first
// tab otherwise, however deep into the screen you were.
const readTab = (search, allowed, fallback) => {
    const wanted = new URLSearchParams(search).get('tab');
    return allowed.indexOf(wanted) >= 0 ? wanted : fallback;
};

const TABS = ['link', 'analytics'];

// A gn2.me link is minted in the background, so the first GET usually comes
// back unbranded. Poll a few times, then leave the long URL on screen.
const POLL_INTERVAL = 2500;
const POLL_LIMIT = 8;

const copyToClipboard = (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(text);
    }
    const field = document.createElement('textarea');
    field.value = text;
    document.body.appendChild(field);
    field.select();
    document.execCommand('copy');
    field.remove();
    return Promise.resolve();
};

// The page's own buttons, drawn as the rest of the product draws them: the
// quiet one is Song Hub's link-bar button to the pixel, and the loud one is
// `.gn-actionbtn` — same red, same 10px corner, same lifted shadow — rebuilt in
// `sx` rather than borrowed as a class, because these sit inside MUI cards next
// to MUI controls and the Bootstrap class is sized for a form footer.
const btnBase = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    border: `1px solid ${palette.line}`,
    borderRadius: `${radius.sm}px`,
    fontSize: 12.5,
    fontWeight: 700,
    padding: '7px 12px',
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    textDecoration: 'none',
    transition: `background-color ${motion.base}, color ${motion.base}, border-color ${motion.base}`,
};

const PrimaryBtn = ({ children, sx = {}, ...rest }) => (
    <Box
        component="button"
        type="button"
        sx={{
            ...btnBase,
            // `.gn-actionbtn`: 10px corner, 10px padding, the same drop shadow.
            bgcolor: palette.brand,
            borderColor: palette.brand,
            color: '#FFFFFF',
            borderRadius: '10px',
            padding: '9px 18px',
            boxShadow: '0px 4px 8px -4px #4C4E646B',
            '&:hover': { bgcolor: palette.brandDark, borderColor: palette.brandDark },
            // Neutral when disabled, not a washed-out red. A 50%-opacity brand
            // button reads as a button that failed to paint; grey reads as
            // "there is nothing to save yet", which is what it means.
            '&:disabled': {
                bgcolor: palette.line,
                // Without this the border stays brand red around a grey fill,
                // which reads as a button mid-repaint.
                borderColor: palette.line,
                color: palette.hint,
                boxShadow: 'none',
                cursor: 'not-allowed',
            },
            ...sx,
        }}
        {...rest}
    >
        {children}
    </Box>
);

const GhostBtn = ({ children, sx = {}, ...rest }) => (
    <Box
        component="button"
        type="button"
        sx={{
            ...btnBase,
            bgcolor: palette.surface,
            color: palette.inkSoft,
            borderColor: palette.line,
            '&:hover': { bgcolor: palette.surfaceAlt, color: palette.ink, borderColor: palette.hint },
            '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
            ...sx,
        }}
        {...rest}
    >
        {children}
    </Box>
);

/**
 * One dimension of the breakdown, drawn in its own form.
 *
 *   no rows     the card explains WHY, when we know why
 *   donut       a share of a small closed set, three colours plus "Other"
 *   bars        an open-ended ranked list, one colour
 *
 * A single category is drawn as a full ring rather than degraded to a stat
 * tile: a 100% donut carries no comparison, but the cards are read as a set,
 * and one of them silently changing shape costs more than the empty comparison
 * does. The exact number sits beside the ring either way.
 */
const DimensionCard = ({ dim, rows }) => {
    // The charts label every row from `name`, and `groupLinkSources` returns
    // its buckets under `label` — so the grouped referrer rows are renamed on
    // the way in rather than rendering as a card full of blank labels.
    const list = dim.group
        ? groupLinkSources(rows).map((b) => ({ ...b, name: b.label }))
        : rows.filter((r) => Number(r.clicks) > 0);

    // Every dimension is readable two ways, and the ranked list is always one
    // of them. Which the OTHER one is comes from the data: `country` has a map,
    // and a share-of-clicks dimension has a ring.
    const alt = dim.map ? 'map' : (dim.form === 'donut' ? 'chart' : null);
    const [view, setView] = useState(alt || 'list');
    const toggleable = Boolean(alt);

    const body = () => {
        // Emptiness is answered BEFORE the view, deliberately. A blank world map
        // is not a finding, it is a map of nothing — and it silently drops the
        // note saying WHY the dimension is empty, which is the whole reason that
        // note exists.
        if (!list.length) {
            return (
                <Typography sx={{ fontSize: 12.5, color: palette.muted, lineHeight: 1.6 }}>
                    {dim.emptyNote || `gn2.me recorded no ${dim.label.toLowerCase()} for these clicks.`}
                </Typography>
            );
        }
        if (view === 'map') {
            // Rendered even for a single country: one shaded shape against the
            // whole world is still a finding — "every click came from India".
            return <ChoroplethMap rows={list} />;
        }
        if (view === 'chart') {
            return <DonutChart rows={foldTail(list, chartPalette.maxSlices)} />;
        }
        return <RankedBars rows={list} max={8} />;
    };

    return (
        <SectionCard
            title={dim.label}
            subtitle={dim.subtitle}
            // Every card in a row ends the same height. Their contents genuinely
            // differ — a donut is taller than a one-row list — and left to itself
            // the grid renders a ragged edge that reads as broken layout rather
            // than as varied data.
            sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
            bodySx={{ flex: 1 }}
            actions={toggleable ? (
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={view}
                    // `v &&` guards the deselect click: clicking the active button
                    // sends null, and honouring that would leave the card with no
                    // view at all.
                    onChange={(e, v) => v && setView(v)}
                    aria-label={`${dim.label} view`}
                    sx={{
                        '& .MuiToggleButton-root': {
                            px: 1.25, py: 0.35, fontSize: 11, fontWeight: 700,
                            letterSpacing: '0.04em', color: palette.muted,
                            borderColor: palette.line,
                        },
                        '& .Mui-selected': {
                            color: `${palette.brand} !important`,
                            bgcolor: `${palette.brandSoft} !important`,
                        },
                    }}
                >
                    <ToggleButton value={alt}>{alt === 'map' ? 'MAP' : 'CHART'}</ToggleButton>
                    <ToggleButton value="list">LIST</ToggleButton>
                </ToggleButtonGroup>
            ) : null}
        >
            {body()}
        </SectionCard>
    );
};

/**
 * One short link, in a dialog — its gn2.me address, where it points, its QR
 * code, and the alias that changes it.
 *
 * Opening this MINTS the link if there is none: `getShareLink` is the ordinary
 * GET, and asking for a song's share link is what creates it. That is the point
 * of the button — a creator presses Share to get an address, not to be told
 * there isn't one — which is exactly why the Insights tab uses the *other*
 * call. Looking at numbers must not create a link; asking to share must.
 *
 * A freshly minted link comes back unbranded while gn2.me registers the code,
 * so this polls a few times and shows the long URL in the meantime rather than
 * an empty box.
 */
export const ShareLinkDialog = ({ open, onClose, surface, songId, subject }) => {
    const [loading, setLoading] = useState(true);
    const [link, setLink] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const [copied, setCopied] = useState(false);

    const [qr, setQr] = useState(null);
    const [qrLoading, setQrLoading] = useState(false);

    const [editing, setEditing] = useState(false);
    const [alias, setAlias] = useState('');
    const [aliasSaving, setAliasSaving] = useState(false);
    const [aliasError, setAliasError] = useState('');
    const [aliasNeedsConfirm, setAliasNeedsConfirm] = useState(false);

    const mounted = useRef(false);
    const pollTimer = useRef(null);
    const pollCount = useRef(0);

    const stopPolling = () => {
        if (pollTimer.current) {
            clearTimeout(pollTimer.current);
            pollTimer.current = null;
        }
    };

    const loadQr = useCallback(async () => {
        setQrLoading(true);
        const response = await getShareLinkQr(surface, songId);
        if (!mounted.current) return;
        setQr(response && response.data ? response.data : { available: false });
        setQrLoading(false);
    }, [surface, songId]);

    const loadLink = useCallback(async (isPoll = false) => {
        if (!isPoll) {
            setLoading(true);
            pollCount.current = 0;
        }
        const response = await getShareLink(surface, songId);
        if (!mounted.current) return;

        // A server error answers with an HTML page rather than the payload, so
        // the shape is checked rather than assumed.
        const body = response && response.data;
        const data = body && typeof body === 'object' && !Array.isArray(body) ? body : null;
        setLoading(false);
        if (!data || (!data.url && !data.reason)) {
            setLink(null);
            setLoadError(true);
            return;
        }
        setLoadError(false);
        setLink(data);
        if (!data.url) return;
        setAlias((current) => (current ? current : (data.alias || '')));

        if (data.branded) {
            stopPolling();
            loadQr();
            return;
        }
        if (pollCount.current < POLL_LIMIT) {
            pollCount.current += 1;
            pollTimer.current = setTimeout(() => loadLink(true), POLL_INTERVAL);
        }
    }, [surface, songId, loadQr]);

    // Nothing is asked for until the dialog is actually opened — the button
    // this hangs off sits on a screen a creator may never press it from, and
    // minting on render would give every one of them a link they never wanted.
    useEffect(() => {
        if (!open || songId === undefined) return undefined;
        mounted.current = true;
        setEditing(false);
        setAliasError('');
        setAliasNeedsConfirm(false);
        loadLink();
        return () => {
            mounted.current = false;
            stopPolling();
        };
    }, [open, songId, loadLink]);

    const shortUrl = (link && link.url) || '';
    // `destination_url` is the payload's own name for the long address the
    // short one redirects to — the same field the page below prints.
    const targetUrl = (link && link.destination_url) || '';
    const pending = Boolean(shortUrl && !link.branded);
    const notShareableReason = link && !link.url ? link.reason : null;

    const handleCopy = () => {
        if (!shortUrl) return;
        copyToClipboard(shortUrl).then(() => {
            if (!mounted.current) return;
            setCopied(true);
            setTimeout(() => mounted.current && setCopied(false), 2000);
        });
    };

    const handleDownloadQr = () => {
        if (!qr || !qr.qr_code_data_uri) return;
        const anchor = document.createElement('a');
        anchor.href = qr.qr_code_data_uri;
        anchor.download = `${(link && link.alias) || 'share'}-qr.${qr.format || 'png'}`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
    };

    const saveAlias = async (confirm = false) => {
        const wanted = alias.trim();
        setAliasError('');
        if (!ALIAS_PATTERN.test(wanted)) {
            setAliasError('Use 3-32 characters: letters, numbers, hyphen or underscore, starting with a letter or number.');
            return;
        }
        setAliasSaving(true);
        const response = await updateShareLinkAlias(surface, songId, wanted, confirm);
        if (!mounted.current) return;
        setAliasSaving(false);

        const data = (response && response.data) || {};
        if (response && response.status === 200) {
            setAliasNeedsConfirm(false);
            setEditing(false);
            setLink(data);
            setAlias(data.alias || wanted);
            // The old QR encodes the old address, so it is refetched, not kept.
            setQr(null);
            if (data.branded) loadQr();
            return;
        }
        if (data.reason === 'alias_change_breaks_existing') {
            setAliasNeedsConfirm(true);
            setAliasError(data.message || 'Changing this stops every link you have already shared from working.');
            return;
        }
        setAliasNeedsConfirm(false);
        setAliasError(
            data.message
            || NOT_SHAREABLE_MESSAGE[data.reason]
            || linkAnalyticsReasonCopy(data.reason)
        );
    };

    // One row of the action list. Icon, label, and whatever the row says on its
    // right — a state word, or nothing.
    const ActionRow = ({ icon, label, note, onClick, disabled }) => (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            disabled={disabled}
            sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 1.25,
                py: 1.35,
                border: 'none',
                borderRadius: `${radius.sm}px`,
                bgcolor: 'transparent',
                color: palette.ink,
                fontSize: 14,
                fontWeight: 600,
                textAlign: 'left',
                cursor: 'pointer',
                transition: `background-color ${motion.base}, color ${motion.base}`,
                '&:hover': { bgcolor: palette.brandSofter, color: palette.brand },
                '&:disabled': { opacity: 0.45, cursor: 'not-allowed' },
            }}
        >
            <Box sx={{ display: 'flex', color: 'inherit' }}>{icon}</Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>{label}</Box>
            {note && (
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: palette.success }}>
                    {note}
                </Typography>
            )}
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: `${radius.lg}px`,
                    boxShadow: shadow.modal,
                    overflow: 'visible',
                },
            }}
        >
            {/* Centred title with the close on the right, the shape every share
                sheet has settled on. */}
            <Box
                sx={{
                    position: 'relative',
                    px: 3,
                    pt: 2.5,
                    pb: 1.5,
                    textAlign: 'center',
                }}
            >
                <Typography sx={{ fontSize: 16, fontWeight: 800, color: palette.ink }}>
                    Share
                </Typography>
                {subject && (
                    <Typography
                        sx={{
                            ...typography.caption,
                            mt: 0.25,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {subject}
                    </Typography>
                )}
                <IconButton
                    onClick={onClose}
                    aria-label="Close"
                    size="small"
                    sx={{
                        position: 'absolute',
                        top: 14,
                        right: 14,
                        bgcolor: palette.surfaceCanvas,
                        color: palette.inkSoft,
                        '&:hover': { bgcolor: palette.brandSoft, color: palette.brand },
                    }}
                >
                    <CloseRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>

            <DialogContent sx={{ px: 3, pb: 3, pt: 0 }}>
                {loading && (
                    <Stack spacing={1.5}>
                        <Skeleton variant="rounded" height={64} />
                        <Skeleton variant="rounded" height={150} />
                        <Skeleton variant="rounded" height={44} />
                    </Stack>
                )}

                {!loading && (loadError || notShareableReason) && (
                    <Alert severity="info" sx={NOTICE_SX}>
                        {notShareableReason
                            ? (NOT_SHAREABLE_MESSAGE[notShareableReason] || linkAnalyticsReasonCopy(notShareableReason))
                            : 'We could not reach the short-link service. Please try again in a moment.'}
                    </Alert>
                )}

                {!loading && shortUrl && (
                    <Stack spacing={2}>
                        {/* The address, in a field rather than as a line of
                            text: it is the one thing on this sheet a creator
                            edits, and the pencil is where they look for that. */}
                        <Box
                            sx={{
                                border: `1px solid ${palette.line}`,
                                borderRadius: `${radius.md}px`,
                                px: 1.75,
                                py: 1.25,
                                bgcolor: palette.surface,
                            }}
                        >
                            <Typography sx={{ ...typography.eyebrow, display: 'block' }}>
                                Short URL
                            </Typography>

                            {editing ? (
                                <>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
                                        <TextField
                                            autoFocus
                                            size="small"
                                            fullWidth
                                            value={alias}
                                            onChange={(event) => { setAlias(event.target.value); setAliasError(''); }}
                                            onKeyDown={(event) => { if (event.key === 'Enter') saveAlias(aliasNeedsConfirm); }}
                                            placeholder="my-song"
                                            inputProps={{ maxLength: 32, 'aria-label': 'Custom short URL' }}
                                            sx={{ '& .MuiInputBase-input': { fontSize: 14, fontWeight: 700 } }}
                                        />
                                        <PrimaryBtn
                                            onClick={() => saveAlias(aliasNeedsConfirm)}
                                            disabled={aliasSaving || !alias.trim()}
                                        >
                                            {aliasSaving ? 'Saving…' : (aliasNeedsConfirm ? 'Confirm' : 'Save')}
                                        </PrimaryBtn>
                                        <GhostBtn
                                            onClick={() => {
                                                setEditing(false);
                                                setAliasError('');
                                                setAliasNeedsConfirm(false);
                                                setAlias((link && link.alias) || '');
                                            }}
                                        >
                                            Cancel
                                        </GhostBtn>
                                    </Stack>
                                    {aliasError && (
                                        <Typography sx={{ fontSize: 12, color: palette.danger, mt: 0.75 }}>
                                            {aliasError}
                                        </Typography>
                                    )}
                                </>
                            ) : (
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.15 }}>
                                    <Typography
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            fontSize: 14.5,
                                            fontWeight: 700,
                                            color: palette.ink,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                        title={shortUrl}
                                    >
                                        {shortUrl.replace(/^https?:\/\//, '')}
                                    </Typography>
                                    <Tooltip arrow title="Change this address">
                                        <IconButton
                                            size="small"
                                            aria-label="Edit short URL"
                                            onClick={() => setEditing(true)}
                                            sx={{ color: palette.inkSoft, '&:hover': { color: palette.brand } }}
                                        >
                                            <EditOutlinedIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            )}
                        </Box>

                        {pending && (
                            <Alert severity="info" sx={NOTICE_SX}>
                                {linkAnalyticsReasonCopy('not_branded')}
                            </Alert>
                        )}

                        {/* Where it actually goes. A short URL is a promise
                            about a destination, and a creator about to paste one
                            into a post is entitled to see which. */}
                        {targetUrl && (
                            <Box sx={{ px: 0.25 }}>
                                <Typography sx={{ ...typography.eyebrow, display: 'block', mb: 0.25 }}>
                                    Original URL
                                </Typography>
                                <Typography
                                    component="a"
                                    href={targetUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        display: 'block',
                                        fontSize: 12.5,
                                        color: palette.muted,
                                        textDecoration: 'none',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        '&:hover': { color: palette.brand, textDecoration: 'underline' },
                                    }}
                                    title={targetUrl}
                                >
                                    {targetUrl}
                                </Typography>
                            </Box>
                        )}

                        {/* The same address, for a camera. */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                py: 1,
                                bgcolor: palette.surfaceCanvas,
                                borderRadius: `${radius.md}px`,
                            }}
                        >
                            {qrLoading && <Skeleton variant="rounded" width={150} height={150} />}
                            {!qrLoading && qr && qr.available && (qr.qr_code_data_uri || qr.qr_code_url) && (
                                <Box
                                    component="img"
                                    src={qr.qr_code_data_uri || qr.qr_code_url}
                                    alt="QR code for this short URL"
                                    sx={{
                                        width: 150,
                                        height: 150,
                                        p: 1,
                                        bgcolor: palette.surface,
                                        border: `1px solid ${palette.line}`,
                                        borderRadius: `${radius.sm}px`,
                                    }}
                                />
                            )}
                            {!qrLoading && (!qr || !qr.available) && (
                                <Typography sx={{ fontSize: 12.5, color: palette.muted, py: 5 }}>
                                    The QR code appears once gn2.me has registered this address.
                                </Typography>
                            )}
                        </Box>

                        <Box>
                            <ActionRow
                                icon={<ContentCopyRoundedIcon sx={{ fontSize: 18 }} />}
                                label="Copy link"
                                note={copied ? 'Copied' : ''}
                                onClick={handleCopy}
                            />
                            <ActionRow
                                icon={<OpenInNewRoundedIcon sx={{ fontSize: 18 }} />}
                                label="Open link"
                                onClick={() => window.open(shortUrl, '_blank', 'noopener')}
                            />
                            <ActionRow
                                icon={<DownloadRoundedIcon sx={{ fontSize: 18 }} />}
                                label="Download QR code"
                                onClick={handleDownloadQr}
                                disabled={!qr || !qr.qr_code_data_uri}
                            />
                        </Box>
                    </Stack>
                )}
            </DialogContent>
        </Dialog>
    );
};

/**
 * Every number gn2.me records against one short link, on whatever screen asks
 * for them.
 *
 * Its own tab here, and the Insights tab of the song's details page: the same
 * analytics either way, because a creator comparing the two should never have
 * to wonder which screen is telling the truth.
 *
 * It answers for the link's existence itself unless the caller already knows.
 * `getShareLinkStatus` is the probe — the ordinary GET MINTS a link, and a tab
 * that creates a gn2.me address merely by being opened is a tab that lies about
 * what it does.
 */
export const LinkAnalytics = ({
    surface, songId, shortUrl, linkState, onCreate,
}) => {
    const [days, setDays] = useState(30);
    const [copiedShortUrl, setCopiedShortUrl] = useState(false);
    const [analytics, setAnalytics] = useState(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    // A view switch, not a filter — it changes how the same numbers are drawn.
    const [trendMode, setTrendMode] = useState('line');
    const [probed, setProbed] = useState(undefined);
    const [windowAnchor, setWindowAnchor] = useState(null);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    // 'ready' | 'pending' | 'none', or undefined while it is still being found
    // out. A caller that has already loaded the link passes `linkState` and
    // spares the probe — including 'loading', which is not the same answer as
    // 'none' and must not render as one; everyone else gets one non-minting GET.
    const told = linkState !== undefined;
    const state = told ? (linkState === 'loading' ? undefined : linkState) : probed;

    useEffect(() => {
        if (told || songId === undefined) return undefined;
        let cancelled = false;
        setProbed(undefined);
        getShareLinkStatus(surface, songId).then((response) => {
            if (cancelled || !mounted.current) return;
            if (!hasShareLink(response)) {
                setProbed('none');
                return;
            }
            setProbed(response.data.branded ? 'ready' : 'pending');
        });
        return () => { cancelled = true; };
    }, [told, surface, songId]);

    const loadAnalytics = useCallback(async (window) => {
        setAnalyticsLoading(true);
        const response = await getShareLinkAnalytics(surface, songId, window);
        if (!mounted.current) return;
        setAnalytics(response && response.data ? response.data : { available: false, reason: 'provider_unavailable' });
        setAnalyticsLoading(false);
    }, [surface, songId]);

    // Read when there is something to read, and not before: a link still being
    // registered has nothing behind it, and one that does not exist would be
    // minted by asking.
    useEffect(() => {
        if (state !== 'ready' || songId === undefined) return;
        loadAnalytics(days);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state, songId, loadAnalytics]);

    const handleCopyShortUrl = () => {
        if (!shortUrl) return;
        copyToClipboard(shortUrl).then(() => {
            if (!mounted.current) return;
            setCopiedShortUrl(true);
            setTimeout(() => mounted.current && setCopiedShortUrl(false), 2000);
        });
    };

    const handleWindowChange = (window) => {
        setDays(window);
        loadAnalytics(window);
    };

    const available = Boolean(analytics && analytics.available);
    const summary = available ? (analytics.summary || {}) : {};
    const dims = available ? (analytics.breakdowns || {}) : {};
    // `Array.isArray` rather than `|| []`: the backend passes gn2.me's payload
    // through without coercing it, so one dimension arriving as something other
    // than a list is the client's to survive. It costs the others nothing.
    const rowsOf = (key) => (Array.isArray(dims[key]) ? dims[key] : []);

    const windowLabel = (LINK_ANALYTICS_DAY_OPTIONS.find((o) => o.value === days) || {})
        .label || 'the selected window';
    const noTraffic = available && !Number(summary.totalClicks);

    // Everything that is not a screen full of numbers is a single block, and a
    // single block belongs INSIDE the panel with the control that produced it —
    // a thin bar floating above a second card saying "no clicks yet" is two
    // cards to say one thing.
    const waiting = state === undefined || (state === 'ready' && !analytics);
    const unavailable = Boolean(!analyticsLoading && analytics && !available);
    const inPanel = waiting || unavailable || state === 'none' || state === 'pending' || noTraffic;

    return (
        <Stack spacing={2.5}>
            {/* One white panel: the control that chooses the window, and
                whatever that window has to say, under one border. */}
            <Box
                sx={{
                    bgcolor: palette.surface,
                    border: `1px solid ${palette.line}`,
                    borderRadius: `${radius.md}px`,
                    boxShadow: shadow.card,
                    overflow: 'hidden',
                }}
            >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                rowGap={1.5}
                columnGap={2}
                sx={{
                    px: 2.5,
                    py: 1.75,
                    borderBottom: inPanel ? `1px solid ${palette.line}` : 'none',
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ ...typography.cardTitle }}>Performance</Typography>
                    {/* Which link these numbers belong to. On the song's own
                        page the analytics are one tab among six, and a screen
                        of counts with no address on it is a screen of counts
                        about nothing in particular. */}
                    {shortUrl && (
                        <Typography
                            component="a"
                            href={shortUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mt: 0.4,
                                fontSize: 12.5,
                                fontWeight: 700,
                                color: palette.brand,
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' },
                            }}
                        >
                            <LinkRoundedIcon sx={{ fontSize: 15 }} />
                            {shortUrl.replace(/^https?:\/\//, '')}
                        </Typography>
                    )}
                </Box>

                {/* One control, out of the way. Four windows are a filter,
                    and a filter nobody is changing should not be spending a
                    third of the bar saying so — which window is showing is
                    named on every card underneath, and beside the icon. */}
                <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
                    <Typography sx={{ ...typography.eyebrow }}>{windowLabel}</Typography>
                    <Tooltip arrow title={`Showing ${windowLabel} — change window`}>
                        <IconButton
                            size="small"
                            aria-label="Change the analytics window"
                            aria-haspopup="true"
                            aria-expanded={Boolean(windowAnchor) || undefined}
                            onClick={(event) => setWindowAnchor(event.currentTarget)}
                            sx={{
                                border: `1px solid ${palette.line}`,
                                borderRadius: `${radius.sm}px`,
                                color: palette.inkSoft,
                                '&:hover': {
                                    bgcolor: palette.brandSofter,
                                    borderColor: palette.brand,
                                    color: palette.brand,
                                },
                            }}
                        >
                            <FilterListRoundedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                    <Menu
                        anchorEl={windowAnchor}
                        open={Boolean(windowAnchor)}
                        onClose={() => setWindowAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        PaperProps={{
                            sx: {
                                minWidth: 160,
                                borderRadius: `${radius.md}px`,
                                boxShadow: shadow.hover,
                                border: `1px solid ${palette.line}`,
                            },
                        }}
                    >
                        {LINK_ANALYTICS_DAY_OPTIONS.map((option) => (
                            <MenuItem
                                key={option.value}
                                selected={days === option.value}
                                onClick={() => {
                                    setWindowAnchor(null);
                                    handleWindowChange(option.value);
                                }}
                                sx={{
                                    fontSize: 13,
                                    fontWeight: days === option.value ? 700 : 500,
                                    color: days === option.value ? palette.brand : palette.inkSoft,
                                    '&.Mui-selected': { bgcolor: palette.brandSofter },
                                    '&.Mui-selected:hover': { bgcolor: palette.brandSoft },
                                }}
                            >
                                {option.label}
                            </MenuItem>
                        ))}
                    </Menu>
                </Stack>
            </Stack>

            {inPanel && (
                <Box sx={{ p: 2.5 }}>
                    {/* The provider answered and there are no numbers —
                        analytics off, or a link it has never issued.
                        Information, not failure. */}
                    {unavailable && (
                        <Alert severity="info" sx={NOTICE_SX}>
                            {linkAnalyticsReasonCopy(analytics.reason)}
                        </Alert>
                    )}

                    {/* Both waits: finding out whether there is a link, and the
                        first read once there is. */}
                    {waiting && (
                        <Stack spacing={2.5}>
                            <Skeleton variant="rounded" height={110} />
                            <Skeleton variant="rounded" height={260} />
                        </Stack>
                    )}

                    {/* There is no link at all yet. Nothing is minted by opening
                        this — a short URL is a thing the creator asks for, not a
                        side effect of looking at a page — so this offers the
                        door rather than walking through it. */}
                    {state === 'none' && (
                        <EmptyState
                            variant="bare"
                            icon={<InsightsOutlinedIcon sx={{ fontSize: 32 }} />}
                            title="No short URL yet"
                            description="Clicks are counted by gn2.me at the redirect, so the numbers start the moment this has a short link to redirect through."
                            action={onCreate ? (
                                <PrimaryBtn onClick={onCreate}>
                                    <LinkRoundedIcon sx={{ fontSize: 17 }} />
                                    Create short URL
                                </PrimaryBtn>
                            ) : null}
                        />
                    )}

                    {/* A link still being registered has no gn2.me code, so
                        there is nothing for the provider to have counted yet. */}
                    {state === 'pending' && (
                        <Alert severity="info" sx={NOTICE_SX}>
                            {linkAnalyticsReasonCopy('not_branded')}
                        </Alert>
                    )}

                    {noTraffic && (
                        <EmptyState
                            variant="bare"
                            icon={<InsightsOutlinedIcon sx={{ fontSize: 32 }} />}
                            title="No clicks yet"
                            description="Share this short URL on Instagram, WhatsApp, or anywhere your listeners are — clicks show up here within minutes."
                            // It says "share this link", so it hands the link
                            // over. Telling someone to do a thing and then
                            // making them go and find it is the gap this closes.
                            action={shortUrl ? (
                                <GhostBtn onClick={handleCopyShortUrl}>
                                    {copiedShortUrl
                                        ? <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                                        : <ContentCopyRoundedIcon sx={{ fontSize: 16 }} />}
                                    {copiedShortUrl ? 'Copied' : 'Copy short URL'}
                                </GhostBtn>
                            ) : null}
                        />
                    )}
                </Box>
            )}
            </Box>

            {available && !noTraffic && (
                // Swapping the window keeps the previous render at reduced
                // opacity instead of flashing a skeleton and jumping the layout.
                <Box
                    sx={{
                        opacity: analyticsLoading ? 0.55 : 1,
                        pointerEvents: analyticsLoading ? 'none' : 'auto',
                        transition: 'opacity 180ms cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                >
                    {/* CSS grid for the same reason the Link tab uses one: MUI's
                        `spacing` is negative margins, and this row has to start
                        and end on the same lines as the full-width cards under
                        it. A grid item stretches by default, which is also what
                        keeps a two-line hint from making one card taller than
                        its neighbours. */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, 1fr)',
                                md: 'repeat(4, 1fr)',
                            },
                            gap: 2,
                        }}
                    >
                        {LINK_ANALYTICS_HERO_FIELDS.map((field) => (
                            <KpiCard
                                key={field.key}
                                icon={HERO[field.key] && HERO[field.key].icon}
                                label={field.label}
                                value={formatLinkMetric(summary[field.key], field.format)}
                                hint={field.hint}
                            />
                        ))}
                    </Box>

                    {/* Click performance, first: "is this growing" is the question
                        people arrive with; "who were they" is the follow-up. */}
                    <Box sx={{ mt: 2.5 }}>
                        <SectionCard
                            title="Click performance"
                            subtitle={
                                `Measured by gn2.me over ${analytics.period || windowLabel}`
                                + (analytics.timezone ? ` · times in ${analytics.timezone}` : '')
                            }
                            actions={
                                <Tabs
                                    value={trendMode}
                                    onChange={(event, value) => setTrendMode(value)}
                                    sx={{
                                        minHeight: 32,
                                        '& .MuiTab-root': {
                                            minHeight: 32, py: 0, fontSize: 12.5, fontWeight: 700,
                                            textTransform: 'none', color: palette.muted,
                                        },
                                        '& .Mui-selected': { color: `${palette.brand} !important` },
                                        '& .MuiTabs-indicator': { bgcolor: palette.brand, height: 2 },
                                    }}
                                >
                                    <Tab value="line" label="Line" />
                                    <Tab value="bar" label="Bar" />
                                </Tabs>
                            }
                        >
                            {rowsOf('date').length > 0 ? (
                                <TrendChart rows={rowsOf('date')} series={SERIES} mode={trendMode} />
                            ) : (
                                <Typography sx={{ fontSize: 12.5, color: palette.muted }}>
                                    gn2.me has not broken these clicks down by day yet.
                                </Typography>
                            )}
                        </SectionCard>
                    </Box>

                    {/* Reach and value. Real data, but the follow-up question
                        rather than the one anyone opened the tab with — so a
                        strip, not four more cards competing with the row above. */}
                    <Box sx={{ mt: 2.5 }}>
                        <SectionCard
                            title="Reach"
                            subtitle={`How far these clicks spread, over ${windowLabel}`}
                        >
                            {/* Four equal columns across the FULL width, with
                                hairline rules between them: four numbers in a row
                                with nothing separating them are read as one number
                                with three others near it. */}
                            <Grid container>
                                {LINK_ANALYTICS_SECONDARY_FIELDS.map((field, index) => (
                                    <Grid
                                        item xs={6} sm={3} key={field.key}
                                        sx={{
                                            px: { xs: 1.5, sm: 2.5 },
                                            py: 1,
                                            // Two columns on a phone, four from sm
                                            // up — so the rules that separate them
                                            // differ per breakpoint. A single
                                            // `index > 0` rule would draw a divider
                                            // down the left of the third stat when
                                            // it starts a new row.
                                            borderLeft: {
                                                xs: index % 2 ? `1px solid ${palette.line}` : 'none',
                                                sm: index ? `1px solid ${palette.line}` : 'none',
                                            },
                                            borderTop: {
                                                xs: index >= 2 ? `1px solid ${palette.line}` : 'none',
                                                sm: 'none',
                                            },
                                            '&:first-of-type': { pl: 0 },
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={0.6}
                                            sx={{ color: palette.hint }}
                                        >
                                            {REACH_ICONS[field.key]}
                                            <Typography sx={{ ...typography.eyebrow }}>{field.label}</Typography>
                                        </Stack>
                                        <Typography sx={{ fontSize: 26, fontWeight: 800, color: palette.ink, mt: 0.35 }}>
                                            {formatLinkMetric(summary[field.key], field.format)}
                                        </Typography>
                                    </Grid>
                                ))}
                            </Grid>
                        </SectionCard>
                    </Box>

                    {/* Every dimension gn2.me records, two to a row. Each card
                        picks its own form from the data it actually has. */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                            gap: 2.5,
                            mt: 2.5,
                        }}
                    >
                        {LINK_ANALYTICS_BREAKDOWNS.map((dim) => (
                            <DimensionCard key={dim.key} dim={dim} rows={rowsOf(dim.key)} />
                        ))}

                        <SectionCard
                            title="Hourly activity"
                            subtitle="Click distribution by hour of day."
                            sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                            bodySx={{ flex: 1 }}
                        >
                            {rowsOf('hourly').length > 0 ? (
                                <ColumnChart rows={rowsOf('hourly')} />
                            ) : (
                                <Typography sx={{ fontSize: 12.5, color: palette.muted }}>
                                    gn2.me has not broken these clicks down by hour yet.
                                </Typography>
                            )}
                        </SectionCard>
                    </Box>
                </Box>
            )}
        </Stack>
    );
};

/**
 * One gn2.me short link, on a page of its own — the address, its QR code, its
 * custom alias, and every number gn2.me records against it.
 *
 * Two routes reach it, and the only difference between them is which surface
 * the link belongs to:
 *
 *   /songhub/short-url                  the creator's music hub
 *   /songhub/music/:slug/short-url      one song
 *
 * This replaced a modal. The analytics below are eleven cards deep and a
 * window the whole tab shares — that is a screen, and putting it in a dialog
 * meant it could not be linked to, refreshed, or opened in a second tab beside
 * the song it describes.
 */
const ShareLinkPage = () => {
    const history = useHistory();
    const location = useLocation();
    const userData = useSelector((state) => state.userData.userData);
    const { slug } = useParams();
    const surface = slug ? SHARE_SURFACE.SONG : SHARE_SURFACE.MUSIC_HUB;

    // The route carries the song's SLUG, because that is the half of the URL a
    // creator recognises — `/songhub/music/midnight-drive` says which song, and
    // `/songhub/music/3672` says nothing at all. The share-link API is
    // keyed by the song's id, so the two have to be reconciled somewhere, and
    // here is the only place that knows both.
    //
    // Navigating from the song list hands the id (and the title) over in router
    // state, which costs nothing. A cold load — a refresh, a bookmark, a link
    // pasted into a second tab — has no state, so the slug is looked up against
    // the creator's own songs below.
    const [song, setSong] = useState(() => {
        if (surface !== SHARE_SURFACE.SONG) return null;
        const passed = location.state || {};
        return passed.songId
            ? { id: passed.songId, title: passed.songTitle || '' }
            : null;
    });
    const [songLookupFailed, setSongLookupFailed] = useState(false);
    const songId = song ? song.id : undefined;
    const songTitle = song ? song.title : '';

    const [tab, setTab] = useState(() => readTab(location.search, TABS, 'link'));

    // `replace`, not `push`: a tab is a view of one screen, and pushing would
    // put every glance at the analytics between the creator and the page they
    // actually came from. The router state rides along so the Back button keeps
    // knowing where that was.
    const changeTab = (value) => {
        setTab(value);
        history.replace({
            pathname: location.pathname,
            search: value === 'link' ? '' : `?tab=${value}`,
            state: location.state,
        });
    };

    const [loading, setLoading] = useState(true);
    const [link, setLink] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const [revoked, setRevoked] = useState(false);

    const [copied, setCopied] = useState(false);

    const [qr, setQr] = useState(null);
    const [qrLoading, setQrLoading] = useState(false);

    const [alias, setAlias] = useState('');
    const [aliasSaving, setAliasSaving] = useState(false);
    const [aliasError, setAliasError] = useState('');
    const [aliasNotice, setAliasNotice] = useState('');
    const [aliasNeedsConfirm, setAliasNeedsConfirm] = useState(false);

    const [confirmRevoke, setConfirmRevoke] = useState(false);
    const [revoking, setRevoking] = useState(false);

    // Polling and every async setState are guarded by these: the creator can
    // navigate away while a request is still in flight.
    const mounted = useRef(false);
    const pollTimer = useRef(null);
    const pollCount = useRef(0);

    const stopPolling = () => {
        if (pollTimer.current) {
            clearTimeout(pollTimer.current);
            pollTimer.current = null;
        }
    };

    const loadQr = useCallback(async () => {
        setQrLoading(true);
        const response = await getShareLinkQr(surface, songId);
        if (!mounted.current) return;
        setQr(response && response.data ? response.data : { available: false, reason: 'provider_unavailable' });
        setQrLoading(false);
    }, [surface, songId]);

    // One GET; while the mint is still in flight (`branded: false`) it schedules
    // the next one itself rather than leaving the creator to reload the page.
    const loadLink = useCallback(async (isPoll = false) => {
        if (!isPoll) {
            setLoading(true);
            pollCount.current = 0;
        }
        const response = await getShareLink(surface, songId);
        if (!mounted.current) return;

        // A server error answers with an HTML page, not the share payload, so
        // the shape is checked rather than assumed — otherwise the page renders
        // nothing at all and looks broken.
        const body = response && response.data;
        const data = body && typeof body === 'object' && !Array.isArray(body) ? body : null;
        setLoading(false);
        if (!data || (!data.url && !data.reason)) {
            setLink(null);
            setLoadError(true);
            return;
        }
        setLoadError(false);
        setLink(data);

        if (!data.url) return;

        setAlias((current) => (current ? current : (data.alias || '')));

        if (data.branded) {
            stopPolling();
            loadQr();
            return;
        }
        if (pollCount.current < POLL_LIMIT) {
            pollCount.current += 1;
            pollTimer.current = setTimeout(() => loadLink(true), POLL_INTERVAL);
        }
    }, [surface, songId, loadQr]);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            stopPolling();
        };
    }, []);

    // The cold-load path: no router state, so the slug has to be turned into an
    // id before anything else can be asked for. The creator's own song list is
    // the only lookup available — there is no by-slug endpoint — so it is
    // fetched once, here, and never on the normal navigation path.
    useEffect(() => {
        if (surface !== SHARE_SURFACE.SONG) return undefined;
        if (song) return undefined;

        let cancelled = false;
        setSongLookupFailed(false);
        handleSongList({ userId: userData && userData.id }, 'GETBYUSER', 200, 0)
            .then((response) => {
                if (cancelled) return;
                const results = (response && response.data && response.data.results) || [];
                const match = results.find((row) => String(row.slug) === String(slug));
                if (match) {
                    setSong({ id: match.id, title: match.songTitle || '' });
                    return;
                }
                // Songs saved before they had a slug are routed by id, and a
                // link shared from the old `/songhub/share-link/3672` shape is
                // still a link someone may have. Both are a bare number, and
                // both should keep working rather than 404.
                if (/^\d+$/.test(String(slug))) {
                    setSong({ id: Number(slug), title: '' });
                    return;
                }
                setSongLookupFailed(true);
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [surface, slug, song, userData && userData.id]);

    useEffect(() => {
        // Nothing can be asked for until the slug has become an id.
        if (surface === SHARE_SURFACE.SONG && songId === undefined) return;
        // A different song is a different link: nothing from the last one
        // survives the switch.
        stopPolling();
        setRevoked(false);
        setLoadError(false);
        setConfirmRevoke(false);
        setAliasError('');
        setAliasNotice('');
        setAliasNeedsConfirm(false);
        setAlias('');
        setQr(null);
        loadLink();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [surface, songId]);

    // A slug that matches nothing the creator owns. Distinct from a link that
    // failed to load: there is no song here to have a link.
    const songMissing = surface === SHARE_SURFACE.SONG && songLookupFailed;

    const handleCopy = () => {
        if (!link || !link.url) return;
        copyToClipboard(link.url).then(() => {
            if (!mounted.current) return;
            setCopied(true);
            setTimeout(() => mounted.current && setCopied(false), 2000);
        });
    };

    const handleDownloadQr = () => {
        if (!qr || !qr.qr_code_data_uri) return;
        const anchor = document.createElement('a');
        anchor.href = qr.qr_code_data_uri;
        anchor.download = `${(link && link.alias) || 'share'}-qr.${qr.format || 'png'}`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
    };

    const saveAlias = async (confirm = false) => {
        const wanted = alias.trim();
        setAliasError('');
        setAliasNotice('');
        if (!ALIAS_PATTERN.test(wanted)) {
            setAliasError('Use 3-32 characters: letters, numbers, hyphen or underscore, starting with a letter or number.');
            return;
        }

        setAliasSaving(true);
        const response = await updateShareLinkAlias(surface, songId, wanted, confirm);
        if (!mounted.current) return;
        setAliasSaving(false);

        const data = (response && response.data) || {};
        const status = response ? response.status : 0;

        if (status === 200) {
            setAliasNeedsConfirm(false);
            setLink(data);
            setAlias(data.alias || wanted);
            setAliasNotice('Your custom short URL is saved.');
            // The old QR encodes the old URL, so it is refetched, not reused.
            setQr(null);
            if (data.branded) loadQr();
            return;
        }

        if (data.reason === 'alias_change_breaks_existing') {
            setAliasNeedsConfirm(true);
            setAliasError(data.message || 'Changing this stops every link you have already shared from working.');
            return;
        }

        setAliasNeedsConfirm(false);
        setAliasError(
            data.message
            || NOT_SHAREABLE_MESSAGE[data.reason]
            || linkAnalyticsReasonCopy(data.reason)
        );
    };

    const handleRevoke = async () => {
        setRevoking(true);
        const response = await revokeShareLink(surface, songId);
        if (!mounted.current) return;
        setRevoking(false);
        setConfirmRevoke(false);
        if (response && (response.status === 200 || response.status === 204)) {
            stopPolling();
            setRevoked(true);
            setLink(null);
            setQr(null);
            setAlias('');
            changeTab('link');
        }
    };

    const notShareableReason = link && !link.url ? link.reason : null;
    const pending = Boolean(link && link.url && !link.branded);

    // Which link this is — the song's or the hub's — said once, in the bar under
    // the heading, rather than folded into a page title that then reads
    // differently on every song.
    // Where "back" goes. Reached from the song's details page, it returns
    // there; reached from the song list, it returns to the list. The return
    // trip carries no `from` of its own, so a second press walks out to Song
    // Hub rather than bouncing between the two screens.
    const cameFrom = (location.state && location.state.from) || null;
    const backLabel = cameFrom ? `Back to ${cameFrom.label}` : 'Back to Song Hub';
    const goBack = () => history.push(
        cameFrom ? cameFrom.path : '/songhub',
        cameFrom ? { songId, songTitle } : undefined
    );

    const isSong = surface === SHARE_SURFACE.SONG;
    const subjectLabel = isSong ? 'Short URL for this song' : 'Short URL for your music page';
    const subjectName = isSong ? (songTitle || 'This song') : 'Your music page';

    const breadcrumbs = [
        { label: 'Song Hub', link: '/songhub' },
        { label: 'Music', link: '/songhub' },
        { label: isSong ? (songTitle || 'Short URL') : 'Short URL', link: location.pathname },
    ];

    // ---------------------------------------------------------------- header

    const header = (
        <>
            {/* The Song Hub header, one level down: title and breadcrumbs left,
                the quiet action opposite. Same white page, same spacing, same
                buttons — this is a screen of that section, not its own product. */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    {/* The back arrow belongs beside the title it takes you
                        back from, the way every detail screen in the reference
                        does it — a button at the far right of the header reads
                        as an action of the page, not a way out of it. */}
                    <Tooltip arrow title={backLabel}>
                        <Box
                            component="button"
                            type="button"
                            onClick={goBack}
                            aria-label={backLabel}
                            sx={{
                                width: 36,
                                height: 36,
                                flexShrink: 0,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: `${radius.sm}px`,
                                border: `1px solid ${palette.line}`,
                                bgcolor: palette.surface,
                                color: palette.inkSoft,
                                cursor: 'pointer',
                                transition: `background-color ${motion.base}, color ${motion.base}, border-color ${motion.base}`,
                                '&:hover': {
                                    bgcolor: palette.surfaceAlt,
                                    color: palette.ink,
                                    borderColor: palette.hint,
                                },
                            }}
                        >
                            <ArrowBackRoundedIcon sx={{ fontSize: 19 }} />
                        </Box>
                    </Tooltip>
                    <Box sx={{ minWidth: 0 }}>
                    <Typography component="h2" sx={{ ...typography.pageTitle, mb: 0 }}>
                        Short URL
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
                        {breadcrumbs.map((crumb, index) => (
                            <React.Fragment key={crumb.label}>
                                {index > 0 && (
                                    <Box component="span" sx={{ px: 1, color: palette.hint, fontSize: 10 }}>
                                        ◆
                                    </Box>
                                )}
                                <Link
                                    to={crumb.link}
                                    style={{ textDecoration: 'none', color: palette.muted, fontSize: '0.8rem' }}
                                >
                                    {crumb.label}
                                </Link>
                            </React.Fragment>
                        ))}
                    </Box>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    {/* The other half of this song's own screens. Only shown for
                        a song: the music hub has no details page. */}
                    {surface === SHARE_SURFACE.SONG && (
                        <GhostBtn
                            onClick={() => history.push(
                                `/songhub/music/${slug}`,
                                {
                                    songId,
                                    songTitle,
                                    from: { path: location.pathname, label: 'Short URL' },
                                }
                            )}
                        >
                            <TuneIcon sx={{ fontSize: 16 }} /> Song details
                        </GhostBtn>
                    )}
                </Box>
            </Box>

            {/* What this link points at, in the bar Song Hub uses for the music
                page's own address. The heading says what the screen is; this
                says which link, which is a different question. */}
            <Box
                sx={{
                    mt: 3,
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    bgcolor: palette.surface,
                    border: `1px solid ${palette.line}`,
                    borderRadius: `${radius.md}px`,
                    px: 2,
                    py: 1.5,
                }}
            >
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        flexShrink: 0,
                        borderRadius: `${radius.sm}px`,
                        bgcolor: palette.brandSoft,
                        color: palette.brand,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <LinkRoundedIcon sx={{ fontSize: 20 }} />
                </Box>

                {/* `flex: 1 1 240px` with `minWidth: 0` is what lets a long
                    address truncate instead of shoving anything off the bar. */}
                <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
                    <Typography sx={{ ...typography.eyebrow, display: 'block', mb: 0.25 }}>
                        {subjectLabel}
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: palette.ink }} noWrap>
                        {subjectName}
                    </Typography>
                </Box>
            </Box>

            {/* The strip gets a card of its own, like the bar above it and
                the panel below: three bands of content sitting straight on the
                page background, with only the middle one unboxed, read as a
                control that had come loose from the layout. */}
            <Box
                sx={{
                    mt: 3,
                    mb: 3,
                    bgcolor: palette.surface,
                    border: `1px solid ${palette.line}`,
                    borderRadius: `${radius.md}px`,
                    boxShadow: shadow.card,
                    px: 1.5,
                    py: 1.25,
                    overflow: 'hidden',
                }}
            >
            <Tabs
                value={tab}
                onChange={(event, value) => changeTab(value)}
                variant="scrollable"
                scrollButtons={false}
                sx={{
                    minHeight: 0,
                    // A segmented control, not an underline: a pill bar reads as
                    // one control with one thing chosen, which is what a tab
                    // strip of three or four short words actually is. Full width
                    // on a phone, only as wide as its own tabs above that.
                    display: { xs: 'flex', sm: 'inline-flex' },
                    width: { xs: '100%', sm: 'auto' },
                    maxWidth: '100%',
                    bgcolor: '#F3F4F6',
                    borderRadius: '999px',
                    p: '4px',
                    '& .MuiTabs-indicator': { display: 'none' },
                    '& .MuiTabs-flexContainer': { gap: '4px' },
                    '& .MuiTab-root': {
                        minHeight: 34,
                        minWidth: 0,
                        py: 0,
                        px: { xs: 1.5, sm: 2 },
                        borderRadius: '999px',
                        fontSize: 13,
                        fontWeight: 700,
                        textTransform: 'none',
                        color: palette.muted,
                        transition: 'background-color 180ms cubic-bezier(0.4, 0, 0.2, 1), color 180ms',
                    },
                    '& .MuiTab-root:hover': { color: palette.ink },
                    '& .Mui-selected': {
                        bgcolor: '#FFFFFF',
                        color: `${palette.brand} !important`,
                        boxShadow: '0 1px 3px rgba(15, 23, 41, 0.12)',
                    },
                    '& .Mui-disabled': { opacity: 0.45 },
                }}
            >
                <Tab
                    value="link"
                    label="Link & QR"
                    icon={<QrCode2RoundedIcon sx={{ fontSize: 17 }} />}
                    iconPosition="start"
                />
                <Tab
                    value="analytics"
                    label="Analytics"
                    icon={<InsightsOutlinedIcon sx={{ fontSize: 17 }} />}
                    iconPosition="start"
                    // Nothing to measure until the link exists. Disabling is
                    // honest about that; an empty tab would invite a bug report.
                    disabled={!link || !link.url}
                />
            </Tabs>
            </Box>
        </>
    );

    // The page wrapper Song Hub uses, so this screen sits on the same white
    // page with the same gutters. `pb-5` is what the last card lands on: the
    // app footer is `fixed-bottom`, an overlay rather than a block after the
    // page, so nothing scrolls clear of it on its own.
    const shell = (children) => (
        <div className="container-fluid py-4 creator-playlist-panel">
            <div className="row m-0">
                <div className="col-12 pb-5">
                    {header}
                    {children}
                </div>
            </div>
        </div>
    );

    // ------------------------------------------------------- states before UI

    // Checked BEFORE `loading`, which is still true here: the link fetch never
    // ran because there is no song to run it for, so without this the page sits
    // on a skeleton that can never resolve.
    if (songMissing) {
        return shell(
            <EmptyState
                icon={<LinkOffRoundedIcon sx={{ fontSize: 32 }} />}
                title="That song could not be found"
                description={`Nothing in your Song Hub has the address "${slug}". It may have been renamed or deleted.`}
                action={<GhostBtn onClick={() => history.push('/songhub')}>Back to Song Hub</GhostBtn>}
            />
        );
    }

    if (loading) {
        return shell(
            <Stack spacing={2.5}>
                <Skeleton variant="rounded" height={128} />
                <Skeleton variant="rounded" height={220} />
            </Stack>
        );
    }

    if (revoked) {
        return shell(
            <EmptyState
                icon={<LinkOffRoundedIcon sx={{ fontSize: 32 }} />}
                title="This short URL has been revoked"
                description="Anyone using the old address will no longer reach this page. You can create a new one whenever you like — it will have a different code."
                action={<PrimaryBtn onClick={() => loadLink()}>Create a new short URL</PrimaryBtn>}
            />
        );
    }

    if (loadError) {
        return shell(
            <EmptyState
                icon={<LinkOffRoundedIcon sx={{ fontSize: 32 }} />}
                title="Your short URL could not be loaded"
                description="Something went wrong reaching the server. Nothing has been changed — try again in a moment."
                action={<PrimaryBtn onClick={() => loadLink()}>Try again</PrimaryBtn>}
            />
        );
    }

    if (notShareableReason) {
        return shell(
            <EmptyState
                icon={<LinkOffRoundedIcon sx={{ fontSize: 32 }} />}
                title="This cannot be shared yet"
                description={NOT_SHAREABLE_MESSAGE[notShareableReason] || 'This page cannot be shared yet.'}
                action={<GhostBtn onClick={() => history.push('/songhub')}>Back to Song Hub</GhostBtn>}
            />
        );
    }

    if (!link || !link.url) return shell(null);

    // -------------------------------------------------------------- link tab

    const linkTab = (
        <Stack spacing={2.5}>
            <SectionCard
                title="Your short URL"
                subtitle={pending
                    ? 'gn2.me is registering this link — the full address works in the meantime.'
                    : `Goes to ${link.destination_url}`}
                actions={pending ? (
                    <Chip
                        size="small"
                        label="Registering…"
                        sx={{
                            height: 22, fontSize: 11, fontWeight: 700,
                            bgcolor: palette.warningSoft, color: '#92400E',
                        }}
                    />
                ) : (
                    <Chip
                        size="small"
                        label="Live"
                        sx={{
                            height: 22, fontSize: 11, fontWeight: 700,
                            bgcolor: palette.successSoft, color: '#065F46',
                        }}
                    />
                )}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                        bgcolor: palette.surfaceAlt,
                        border: `1px solid ${palette.line}`,
                        borderRadius: `${radius.sm}px`,
                        px: 2, py: 1.5,
                    }}
                >
                    <Typography
                        sx={{
                            flex: 1, minWidth: 0,
                            fontFamily: 'monospace', fontSize: 15, fontWeight: 700,
                            color: palette.brand,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                        title={link.url}
                    >
                        {link.url}
                    </Typography>
                    <Tooltip title={copied ? 'Copied' : 'Copy'} arrow>
                        <Box component="span">
                            <GhostBtn onClick={handleCopy} aria-label="Copy short URL">
                                <ContentCopyRoundedIcon sx={{ fontSize: 16 }} />
                                {copied ? 'Copied' : 'Copy'}
                            </GhostBtn>
                        </Box>
                    </Tooltip>
                    <Tooltip title="Open in a new tab" arrow>
                        <Box
                            component="a"
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                ...btnBase,
                                textDecoration: 'none',
                                bgcolor: palette.surface,
                                color: palette.inkSoft,
                                borderColor: palette.line,
                                '&:hover': { bgcolor: palette.surfaceAlt, color: palette.ink },
                            }}
                        >
                            <OpenInNewRoundedIcon sx={{ fontSize: 16 }} /> Open
                        </Box>
                    </Tooltip>
                </Stack>
            </SectionCard>

            {/* CSS grid, NOT `<Grid container spacing>`.
                MUI's Grid spaces itself with negative margins — `margin-left:
                -20px` and `width: calc(100% + 20px)`, with the items padded
                back in — so a Grid row sits 20px wider and 20px higher than the
                plain full-width cards above and below it. That is what put this
                pair out of line with "Your short URL" and "Revoke": the cards
                were fine, the row around them was not. `gap` adds no margins at
                all, so these edges land exactly where every other card's do,
                and the two columns still stretch to equal height. */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2.5,
                }}
            >
                    <SectionCard
                        title="QR code"
                        subtitle="Print it, or put it on screen — it resolves to the same short URL."
                    >
                        {qrLoading && <Skeleton variant="rounded" width={150} height={150} />}
                        {!qrLoading && qr && qr.available && (qr.qr_code_data_uri || qr.qr_code_url) && (
                            // The code and its button in a ROW. Stacked, the card
                            // ran half again as tall as the one beside it and the
                            // difference had to go somewhere — this spends the
                            // width the card already has instead.
                            <Stack direction="row" spacing={2.5} alignItems="center" flexWrap="wrap" rowGap={2}>
                                <Box
                                    component="img"
                                    src={qr.qr_code_data_uri || qr.qr_code_url}
                                    alt="QR code for this short URL"
                                    sx={{
                                        width: 150, height: 150,
                                        border: `1px solid ${palette.line}`,
                                        borderRadius: `${radius.sm}px`,
                                        bgcolor: palette.surface,
                                        p: 1,
                                        flexShrink: 0,
                                    }}
                                />
                                {qr.qr_code_data_uri && (
                                    <GhostBtn onClick={handleDownloadQr}>
                                        <DownloadRoundedIcon sx={{ fontSize: 16 }} /> Download PNG
                                    </GhostBtn>
                                )}
                            </Stack>
                        )}
                        {!qrLoading && qr && !qr.available && (
                            <Typography sx={{ fontSize: 12.5, color: palette.muted, lineHeight: 1.6 }}>
                                {linkAnalyticsReasonCopy(qr.reason)}
                            </Typography>
                        )}
                        {!qrLoading && !qr && (
                            <Typography sx={{ fontSize: 12.5, color: palette.muted, lineHeight: 1.6 }}>
                                The QR code appears once your short URL is registered.
                            </Typography>
                        )}
                    </SectionCard>

                    <SectionCard
                        title="Custom alias URL"
                        subtitle="Replace the generated code with something people can read out loud."
                    >
                        <Stack spacing={1.5}>
                            <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap" rowGap={1.5}>
                                <TextField
                                    size="small"
                                    value={alias}
                                    placeholder="my-new-single"
                                    onChange={(event) => {
                                        setAlias(event.target.value.trim());
                                        setAliasNeedsConfirm(false);
                                        setAliasError('');
                                        setAliasNotice('');
                                    }}
                                    error={Boolean(aliasError)}
                                    inputProps={{ 'aria-label': 'Custom alias URL' }}
                                    sx={{ maxWidth: 280, flex: 1, bgcolor: palette.surface }}
                                />
                                <PrimaryBtn
                                    disabled={aliasSaving || !alias || alias === (link.alias || '')}
                                    onClick={() => saveAlias(false)}
                                    sx={{ py: '9px' }}
                                >
                                    {aliasSaving ? 'Saving…' : 'Save'}
                                </PrimaryBtn>
                            </Stack>

                            {aliasError && (
                                <Alert severity="warning" sx={{ borderRadius: `${radius.sm}px`, fontSize: 13, py: 0.25 }}>
                                    {aliasError}
                                </Alert>
                            )}
                            {aliasNeedsConfirm && (
                                <Box>
                                    <GhostBtn disabled={aliasSaving} onClick={() => saveAlias(true)}>
                                        Change it anyway
                                    </GhostBtn>
                                </Box>
                            )}
                            {aliasNotice && (
                                <Alert severity="success" sx={{ borderRadius: `${radius.sm}px`, fontSize: 13, py: 0.25 }}>
                                    {aliasNotice}
                                </Alert>
                            )}
                            <Typography sx={{ fontSize: 12, color: palette.muted, lineHeight: 1.6 }}>
                                3–32 characters: letters, numbers, hyphen or underscore, starting with a
                                letter or number.
                            </Typography>
                        </Stack>
                    </SectionCard>
            </Box>

            {/* Full width, and last. Revoking is the one thing on this tab that
                cannot be undone, so it sits apart from the two controls people
                actually came to use rather than tucked beside them. */}
            <SectionCard
                title="Revoke this short URL"
                subtitle="Retires the address at gn2.me. Every copy you have already shared stops working."
            >
                {!confirmRevoke ? (
                    <GhostBtn
                        onClick={() => setConfirmRevoke(true)}
                        sx={{
                            color: palette.danger,
                            borderColor: palette.dangerSoft,
                            '&:hover': { bgcolor: palette.dangerSoft, color: palette.danger },
                        }}
                    >
                        <LinkOffRoundedIcon sx={{ fontSize: 16 }} /> Revoke
                    </GhostBtn>
                ) : (
                    <Stack spacing={1.5} alignItems="flex-start">
                        <Alert severity="error" sx={{ borderRadius: `${radius.sm}px`, fontSize: 13 }}>
                            Revoke this short URL? Every copy you have already shared — posts, printed
                            QR codes, bios — stops working. This cannot be undone.
                        </Alert>
                        <Stack direction="row" spacing={1.5}>
                            <GhostBtn onClick={() => setConfirmRevoke(false)}>Cancel</GhostBtn>
                            <PrimaryBtn
                                disabled={revoking}
                                onClick={handleRevoke}
                                sx={{ bgcolor: palette.danger, '&:hover': { bgcolor: '#DC2626' } }}
                            >
                                {revoking ? 'Revoking…' : 'Revoke'}
                            </PrimaryBtn>
                        </Stack>
                    </Stack>
                )}
            </SectionCard>
        </Stack>
    );

    return shell(tab === 'link' ? linkTab : (
        <LinkAnalytics
            surface={surface}
            songId={songId}
            shortUrl={link && link.url}
            linkState={loading
                ? 'loading'
                : (link && link.branded ? 'ready' : (pending ? 'pending' : 'none'))}
        />
    ));
};

export default ShareLinkPage;
